/**
 * Apex dashboard page — live Apex telemetry (monitor-only build).
 *
 * All hardware control was removed from this app: no outlet toggles,
 * no kill switch, no confirm modals, no advanced mode. The page polls
 * the agent's GET /state endpoint and renders read-only telemetry:
 *
 *   - Critical probe tiles + sparkline history
 *   - Outlet status display (state, mode, intensity, safety badge)
 *   - Power panel
 *   - Controller identity / feed / modules
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../components/Card';
import { ApexControllerInfo } from '../components/ApexControllerInfo';
import { ApexOutletGroup } from '../components/ApexOutletGroup';
import { ApexPowerPanel, classifyPowerProbe } from '../components/ApexPowerPanel';
import { ApexProbeTile } from '../components/ApexProbeTile';
import {
  OUTLET_GROUPS,
  OUTLET_GROUP_ORDER,
  groupOutlets,
} from '../lib/apex-grouping';
import { apexConnectionStatus, formatAgeSeconds } from '../lib/apex-status';
import { useProbeHistory } from '../lib/probe-history';
import {
  CRITICAL_PROBE_ORDER,
  resolveProbeId,
  type ProbeId,
} from '../lib/probe-thresholds';
import { devApi } from '../lib/dev-api';
import { loadPublicEnv } from '../lib/env';
import type { AgentSnapshot, ApexProbeSnapshot } from '../lib/types';

// Tank label — hardcoded for M0.6, will move to per-tank config in M1.
const TANK_LABEL = '180 Gallon';
// Apex poll cadence is fixed by the agent at 5s; the dashboard re-fetches
// /state every 2s but the underlying Apex data only refreshes every 5s.
const APEX_POLL_INTERVAL_S = 5;
const DASHBOARD_POLL_INTERVAL_MS = 2_000;
const NOW_TICK_INTERVAL_MS = 1_000;

const CONNECTION_BADGE_VARIANT = {
  connected: 'ok',
  stale: 'warn',
  offline: 'danger',
} as const;

const CONNECTION_BADGE_LABEL = {
  connected: 'connected',
  stale: 'stale',
  offline: 'offline',
} as const;

/**
 * Pick the first probe that matches each canonical id, in priority order.
 */
function pickCriticalProbes(probes: readonly ApexProbeSnapshot[]): Map<ProbeId, ApexProbeSnapshot> {
  const out = new Map<ProbeId, ApexProbeSnapshot>();
  for (const probe of probes) {
    const id = resolveProbeId(probe.name);
    if (!id || out.has(id)) continue;
    out.set(id, probe);
  }
  return out;
}

export function ApexDashboardPage() {
  const env = useMemo(() => loadPublicEnv(), []);
  const [snapshot, setSnapshot] = useState<AgentSnapshot | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const history = useProbeHistory();
  const lastRecordedAtRef = useRef<number | undefined>(undefined);

  // ── /state polling (agent read-only endpoint) ──────────────────────
  useEffect(() => {
    let cancelled = false;
    async function tick(): Promise<void> {
      const res = await devApi.state();
      if (cancelled) return;
      if (res.ok) {
        setSnapshot(res.value);
        setLastError(null);
      } else {
        setLastError(res.error);
      }
    }
    void tick();
    const id = window.setInterval(() => void tick(), DASHBOARD_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // ── 1s "now" heartbeat for staleness display ───────────────────────
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), NOW_TICK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // Feed probe values into the sparkline history buffer.
  useEffect(() => {
    const apex = snapshot?.apex;
    if (!apex) return;
    if (apex.fetchedAtMs === lastRecordedAtRef.current) return;
    lastRecordedAtRef.current = apex.fetchedAtMs;
    history.record(apex.probes, apex.fetchedAtMs);
  }, [snapshot, history]);

  const apex = snapshot?.apex;
  const connection = apexConnectionStatus(apex?.fetchedAtMs, nowMs);
  const ageLabel = formatAgeSeconds(apex?.fetchedAtMs, nowMs);
  const criticals = useMemo(
    () => pickCriticalProbes(apex?.probes ?? []),
    [apex?.probes],
  );
  const grouped = useMemo(
    () => (apex ? groupOutlets(apex.outlets) : new Map()),
    [apex],
  );
  const powerProbes = useMemo(
    () => (apex?.probes ?? []).filter((p) => classifyPowerProbe(p) !== null),
    [apex?.probes],
  );

  return (
    <div className="min-h-screen px-6 py-8 max-w-6xl mx-auto">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight">ReefApp</h1>
          <span className="text-reef-400">·</span>
          <span className="text-sm text-reef-200">{TANK_LABEL}</span>
          <span className="text-reef-400">·</span>
          <span className="text-sm text-reef-200">Apex</span>
          <span className="text-reef-400">·</span>
          <span
            className="text-xs text-reef-300 tabular-nums"
            data-testid="apex-age-label"
          >
            last poll {ageLabel} ago
          </span>
          <Badge
            variant={CONNECTION_BADGE_VARIANT[connection]}
            live={connection === 'connected'}
          >
            {CONNECTION_BADGE_LABEL[connection]}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {env.devPanelEnabled ? (
            <Link to="/dev">
              <Button variant="ghost">Dev panel</Button>
            </Link>
          ) : null}
        </div>
      </header>

      {lastError ? (
        <div className="mb-4 rounded-md border border-coral-500/40 bg-coral-500/10 px-3 py-2 text-xs text-coral-200">
          {lastError}
        </div>
      ) : null}

      {!snapshot ? (
        <Card>
          <CardHeader>
            <CardTitle>Waiting for agent…</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-reef-300">
              Start the agent with{' '}
              <code className="text-reef-100">pnpm --filter @reefapp/agent dev</code>{' '}
              to begin streaming Apex telemetry to this dashboard.
            </p>
          </CardBody>
        </Card>
      ) : !apex ? (
        <Card data-testid="apex-empty-state">
          <CardHeader>
            <CardTitle>No Apex bridge configured</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-reef-300">
              The agent is online but no Apex snapshot has been published yet.
              Configure <code className="text-reef-100">APEX_HOST</code>{' '}
              in the agent's environment and restart it, then refresh this page.
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-6">
          <section aria-labelledby="critical-probes-heading">
            <h2
              id="critical-probes-heading"
              className="mb-3 text-xs uppercase tracking-wider text-reef-400"
            >
              Critical Probes
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {CRITICAL_PROBE_ORDER.map((id) => {
                const probe = criticals.get(id);
                if (!probe) return null;
                return (
                  <ApexProbeTile
                    key={id}
                    id={id}
                    probe={probe}
                    history={history.series(probe)}
                  />
                );
              })}
            </div>
          </section>

          {/* Controller identity / feed / modules — modern REST only. */}
          <ApexControllerInfo
            generation={apex.generation}
            system={apex.system}
            feed={apex.feed}
            modules={apex.modules}
          />

          <section aria-labelledby="outlets-heading">
            <h2
              id="outlets-heading"
              className="mb-3 text-xs uppercase tracking-wider text-reef-400"
            >
              Outlets
            </h2>
            <div className="grid gap-3">
              {OUTLET_GROUP_ORDER.map((groupId) => {
                const outlets = grouped.get(groupId);
                if (!outlets || outlets.length === 0) return null;
                const meta = OUTLET_GROUPS[groupId];
                return (
                  <ApexOutletGroup
                    key={groupId}
                    groupId={groupId}
                    label={meta.label}
                    outlets={outlets}
                    defaultCollapsed={meta.defaultCollapsed}
                  />
                );
              })}
            </div>
          </section>

          <section aria-labelledby="power-heading">
            <h2
              id="power-heading"
              className="mb-3 text-xs uppercase tracking-wider text-reef-400"
            >
              Power
            </h2>
            <ApexPowerPanel probes={powerProbes} />
          </section>

          <footer
            className="text-xs text-reef-500 tabular-nums flex flex-wrap items-center gap-x-3 gap-y-1"
            data-testid="apex-footer"
          >
            <span>{snapshot.ts}</span>
            <span>·</span>
            <span>
              apex-bridge:{' '}
              <span
                className={
                  connection === 'connected'
                    ? 'text-emerald-400'
                    : connection === 'stale'
                      ? 'text-amber-400'
                      : 'text-coral-400'
                }
              >
                {CONNECTION_BADGE_LABEL[connection]}
              </span>
            </span>
            <span>·</span>
            <span>{apex.outlets.length} outlets</span>
            <span>·</span>
            <span>{apex.probes.length} probes</span>
            <span>·</span>
            <span>poll {APEX_POLL_INTERVAL_S}s</span>
          </footer>
        </div>
      )}
    </div>
  );
}
